export const permissionArray: string[] = [
  "add user",
  "get user",
  "get user list",
  "update user",
  "delete user",
  "add role",
  "get role",
  "get role list",
  "update role",
  "delete role",
  "delete permission",
  "add permission",
  "get permission",
  "get permission list",
  "get role permission list",
  "add role permission list",
  "get role permission",
  "update role permission",
  "delete role permission",
  "get category",
  "delete category",
  "get category list",
  "add category list",
  "update category",
  "update product",
  "add product",
  "get product",
  "get product list",
  "delete product",
  "delete product image",
  "get product image",
  "get product image list",
  "add product image",
  "update product image",
  "create single sub-category",
  "fetch single sub-category",
  "update single sub-category",
  "get sub-category list",
  "delete sub-category",
  "create single vendor",
  "delete single vendor",
  "get single vendor",
  "get vendor list",
  "update vendor list",
  "get dashboard analytics",
  "get top product",
  "get near expiry product",
  "get sales report",
  "update campaign status",
  "update vendor status",
  "get order list",
  "update order status",
  "update order payment status",
  "update order delivery status",
  "get delivery status of order",
];

export const getSupabaseAuthStorageKey = (): string => {
  const url = env("VITE_SUPABASE_URL");
  const projectRef = url.match(/^https?:\/\/([^.]+)\./)?.[1] ?? "";
  return `sb-${projectRef}-auth-token`;
};

export const getToken = (): string | null => {
  try {
    const raw = localStorage.getItem(getSupabaseAuthStorageKey());
    if (!raw) return null;
    return JSON.parse(raw)?.access_token ?? null;
  } catch {
    return null;
  }
};

export interface AppUser {
  id: string;
  email: string;
}

export const getAppUser = (): AppUser | null => {
  try {
    const raw = localStorage.getItem(getSupabaseAuthStorageKey());
    if (!raw) return null;
    const user = JSON.parse(raw)?.user;
    if (!user?.id || !user?.email) return null;
    return { id: user.id, email: user.email };
  } catch {
    return null;
  }
};

interface UserData {
  email?: string;
  userId?: string;
  userName?: string;
  Permissions?: string[];
  [key: string]: any;
}
export const getCurrentUser = (key?: keyof UserData) => {
  const data = JSON.parse(localStorage.getItem("user") || "{}");
  if (!key) return data;
  return data[key];
};

export const clearOnLogout = () => {
  localStorage.removeItem(getSupabaseAuthStorageKey());
  localStorage.removeItem("user");
};

export const combineClasses = (...classes: (string | undefined | false)[]) =>
  classes.filter(Boolean).join(" ");

export const env = (key: keyof ImportMetaEnv, defaultValue = ""): string => {
  const value = import.meta.env[key];
  return typeof value === "string" ? value : defaultValue;
};

export const requiredMessage = (name: string) => `${name} is required!`;

export const onlyNumbersInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (["e", "E", "+", "-", "."].includes(e.key)) {
    e.preventDefault();
  }
};

export const capitalizeText = (text?: string) => {
  if (!text) return "";
  return text
    .split(" ")
    .map((word) => {
      const [first, ...rest] = word;
      return first ? first.toUpperCase() + rest.join("") : "";
    })
    .join(" ");
};

export const CURRENCY_SYMBOL = "₹";

export const getInitialValues = <T extends Record<string, any>>(config: T) =>
  Object.fromEntries(
    Object.entries(config).map(([key, val]) => [key, val.defaultValue])
  );

export const getSelectOptions = (arr?: { label: any; value: any }[]) =>
  (arr ?? []).map((item) => ({
    label: String(item.label),
    value: String(item.value),
  }));

export const createEnum = (obj: Record<string, any>) => {
  Object.freeze(obj);
};

export const dateConverter = (str: string | Date) => {
  const d = new Date(str);

  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const dateTimeConverter = (str: string | Date) => {
  const d = new Date(str);

  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  return `${dateConverter(d)}, ${time}`;
};

export const inputDateFormat = (date?: string | Date) =>
  date ? new Date(date).toISOString().split("T")[0] : "";

export const updateParams = (
  params: Record<string, any>,
  setParams: React.Dispatch<React.SetStateAction<Map<string, any>>>
) => {
  setParams((prev) => {
    Object.entries(params || {}).forEach(([key, value]) => {
      prev.set(key, value);
    });
    return prev;
  });
};

export const addBaseUrl = (url?: string) => {
  return url ? `${env("VITE_API_BASE_URL")}/${url}` : "";
};

export const checkPermission = (permission?: string) => {
  const permissions = getCurrentUser("permissions") || [];
  return permissions.includes(permission?.toLowerCase?.());
};

export const conditionalColumns = <T>(
  condition: boolean,
  columns: T[] | T
): T[] => {
  const colArray = Array.isArray(columns) ? columns : [columns];
  return condition ? colArray : [];
};

export const formatNumberWithSuffix = (value, roundDigits = 1) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(roundDigits)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(roundDigits)}k`;
  return value.toString();
};

export const formatIndianCurrency = (minorUnits: number, roundDigits = 2) => {
  const rupees = minorUnits / 100;

  if (rupees >= 1_00_00_000) {
    return `${CURRENCY_SYMBOL}${(rupees / 1_00_00_000).toFixed(roundDigits)}Cr`;
  }
  if (rupees >= 1_00_000) {
    return `${CURRENCY_SYMBOL}${(rupees / 1_00_000).toFixed(roundDigits)}L`;
  }

  return `${CURRENCY_SYMBOL}${rupees.toLocaleString("en-IN")}`;
};

export const formatDate = (date: Date) =>
  date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export const formatRoleLabel = (role: string) =>
  role
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const formatRelativeDays = (date: Date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "today";
  if (diffDays === 1) return "tomorrow";
  if (diffDays > 1) return `in ${diffDays} days`;
  if (diffDays === -1) return "yesterday";
  return `${Math.abs(diffDays)} days ago`;
};
